import { computed, effect, inject, untracked } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { AuthStore } from '@account/data-access';
import { ErrorStore } from '@application/errors/data-access';
import { setPropError, setPropLoaded, setPropLoading, withReqState } from '@application/data-access';
import { TPersonModule, TPersonProductValidity, SystemModules } from './entities/system-modules.model';
import { SystemModulesService } from './infra/system-modules.service';
import { getDateTime, isAdminScope } from '@shared/utils';

import { DateTime } from 'luxon';

export type TModuleCheckStatus = 'init' | 'loading' | 'success' | 'error';

export type TModuleAccessCheckResult =
  | { status: 'allowed'; modules: TPersonModule[] }
  | { status: 'missing'; modules: TPersonModule[] }
  | { status: 'transient_error'; modules: TPersonModule[] | null; error?: unknown };

type TModulesRequestResult =
  | { status: 'success' | 'cache'; modules: TPersonModule[] }
  | { status: 'error'; modules: TPersonModule[] | null; error: unknown };

export type TSystemModulesStore = {
  modules: TPersonModule[];
  allowedModuleIds: Set<string>;
  isConnectionError: boolean;
  lastSuccessfulModules: TPersonModule[];
  hasSuccessfulSnapshot: boolean;
  lastSuccessfulAt: number | null;
  lastCheckStatus: TModuleCheckStatus;
  lastCheckError: unknown | null;
  productsValidity: TPersonProductValidity[];
};

export const initialSystemModulesStore: TSystemModulesStore = {
  modules: [],
  allowedModuleIds: new Set<string>(),
  isConnectionError: false,
  lastSuccessfulModules: [],
  hasSuccessfulSnapshot: false,
  lastSuccessfulAt: null,
  lastCheckStatus: 'init',
  lastCheckError: null,
  productsValidity: [],
};

const MODULE_NAME_MAPPING: Record<string, string> = {
  '45c45478-0efc-4def-8ff9-7636e3e72c18': 'Actuar',
  '24a44ab0-4d95-4e98-9eb5-587553c5d7e4': 'Acesso',
  '3085ce66-a968-49ee-a88e-b5b9c7e2e59a': 'AvaliacaoFisica',
  '38299a3b-5e4c-447a-ba9a-ab9e7f19b4d2': 'Treino',
  'b733f377-b7b1-4701-b654-62ee6d835137': 'Cobranca',
  '475658a0-236b-4772-b77b-e771ecca66d2': 'Gestor',
  'aecd8154-509a-4eec-9533-0ef14b975e35': 'Helpdesk',
  'f45c6900-64d6-4e04-a273-46a410501532': 'ActuarWeb',
  '6e2a0f78-a58f-47be-8f91-85babcbe5b9f': 'Crm',
  '033ac824-6786-4071-9180-e9c10d742ef1': 'Facial',
  '6be95655-afba-492d-90d4-4637397b3b84': 'Contratos',
  '78567d75-39c1-43df-9ece-cc32ff554de2': 'Gympass',
  '2322c49a-0ba8-4056-9854-3892edfe3f16': 'NotaFiscal',
  '7d306ae7-3a62-4d2e-bbb7-ec973f029a84': 'Totalpass',
  'd8fda503-805e-4746-8003-f2c050b03321': 'Whatsapp',
  'c4fda503-805e-4746-8003-f2c050b03578': 'Integrations',
  '9c8d007f-fe34-442e-ba8a-1f1dc12f5282': 'Reservas',
  '9963dd95-6300-4b9a-bd96-3776b42fddca': 'Servicos',
  '6df2bc9f-5653-409d-a8b9-d9baa6e8cc45': 'Mercadorias',
  'c286fddd-2ec1-4799-baea-cbfd4ad84d66': 'Financeiro',
};

const getAllowedModuleIds = (modules: TPersonModule[]): Set<string> =>
  new Set<string>(
    modules
      .filter((module) => module.IsActive && module.IsValid)
      .map((module) => module.ProductId)
  );

const hasRequiredModules = (
  modules: TPersonModule[] | null,
  requiredModules: Array<SystemModules | string>
): boolean => {
  if (!modules) {
    return false;
  }

  const allowedIds = getAllowedModuleIds(modules);
  return requiredModules.every((moduleId) => allowedIds.has(String(moduleId)));
};

const isConnectionErrorResponse = (error: unknown): boolean =>
  error instanceof HttpErrorResponse && (error.status === 0 || !navigator.onLine);

export const SystemModulesStore = signalStore(
  { providedIn: 'root' },
  withState<TSystemModulesStore>(initialSystemModulesStore),
  withReqState('loadPersonModules', 'loadProductsValidity'),
  withComputed((store) => ({
    activeModules: computed(() =>
      store.modules().filter((module) => module.IsActive && module.IsValid)
    ),

    hasActiveModules: computed(() =>
      store.modules().some((module) => module.IsActive && module.IsValid)
    ),

    /**
     * Data em que o SISTEMA e bloqueado — a validade do produto "Actuar Web".
     *
     * E esse produto, e nao o mais proximo de expirar, porque e o que a propria aplicacao usa para
     * decidir se ha acesso: `isModuleAllowed(SystemModules.ActuarWeb)` e o que gateia a
     * `lib-modal-expiration` e a modal de faturas. Perder "Acesso" ou "Trei.no" tira um modulo;
     * perder este tira o sistema.
     *
     * A correspondencia e por `ProductId` porque o enum `SystemModules` E uma lista de ProductId —
     * nao ha aqui nome a comparar nem mapa de alias a inventar.
     */
    systemBlockDate: computed<string | null>(
      () =>
        store
          .productsValidity()
          .find((product) => product.ProductId === SystemModules.ActuarWeb)
          ?.AvailableUntilUtc ?? null
    ),

    /**
     * Dias inteiros ate ao bloqueio. Negativo quando o prazo ja passou — e um estado alcancavel:
     * a revogacao do modulo nao e instantanea, entao ha uma janela em que o prazo terminou e o
     * sistema ainda responde. Colapsar isso a zero escondia justamente o caso mais urgente.
     *
     * Conta-se de meia-noite a meia-noite: para quem le "faltam 2 dias", o que interessa e o dia
     * do calendario, nao as horas que sobram do dia de hoje.
     */
    daysUntilSystemBlock: computed<number | null>(() => {
      const blockDate = store
        .productsValidity()
        .find((product) => product.ProductId === SystemModules.ActuarWeb)?.AvailableUntilUtc;

      if (!blockDate) {
        return null;
      }

      const parsed = getDateTime(blockDate);
      if (!parsed?.isValid) {
        return null;
      }

      return Math.round(parsed.startOf('day').diff(DateTime.now().startOf('day'), 'days').days);
    }),

    moduleNames: computed(() =>
      store.modules()
        .filter((module) => module.IsActive && module.IsValid)
        .map((module) => MODULE_NAME_MAPPING[module.ProductId])
        .filter(Boolean)
    ),
  })),
  withMethods(
    (
      store,
      systemModulesService = inject(SystemModulesService),
      errorStore = inject(ErrorStore)
    ) => {
      let activeModulesRequest: Promise<TModulesRequestResult> | null = null;

      const getSnapshotModules = (): TPersonModule[] => store.lastSuccessfulModules();

      const setSuccessfulModules = (modules: TPersonModule[]): void => {
        patchState(
          store,
          {
            modules,
            allowedModuleIds: getAllowedModuleIds(modules),
            lastSuccessfulModules: modules,
            hasSuccessfulSnapshot: true,
            lastSuccessfulAt: DateTime.now().toMillis(),
            isConnectionError: false,
            lastCheckStatus: 'success',
            lastCheckError: null,
          },
          setPropLoaded('loadPersonModules')
        );
      };

      const setModulesError = (error: unknown): void => {
        const isConnectionError = isConnectionErrorResponse(error);

        patchState(
          store,
          {
            isConnectionError,
            lastCheckStatus: 'error',
            lastCheckError: error,
          },
          setPropError('loadPersonModules', error)
        );

        if (isConnectionError) {
          return;
        }

        if (error instanceof HttpErrorResponse) {
          errorStore.setHttpError(error);
          return;
        }

        errorStore.setJavascriptError(error);
      };

      const requestModules = async (
        options: { force?: boolean } = {}
      ): Promise<TModulesRequestResult> => {
        if (activeModulesRequest) {
          return activeModulesRequest;
        }

        if (!options.force && store.hasSuccessfulSnapshot()) {
          return {
            status: 'cache',
            modules: getSnapshotModules(),
          } satisfies TModulesRequestResult;
        }

        patchState(
          store,
          {
            isConnectionError: false,
            lastCheckStatus: 'loading',
            lastCheckError: null,
          },
          setPropLoading('loadPersonModules')
        );

        activeModulesRequest = firstValueFrom(systemModulesService.getPersonModules())
          .then((modules) => {
            setSuccessfulModules(modules);
            return {
              status: 'success',
              modules,
            } satisfies TModulesRequestResult;
          })
          .catch((error: unknown) => {
            setModulesError(error);
            return {
              status: 'error',
              modules: store.hasSuccessfulSnapshot() ? getSnapshotModules() : null,
              error,
            } satisfies TModulesRequestResult;
          })
          .finally(() => {
            activeModulesRequest = null;
          });

        return activeModulesRequest;
      };

      return {
        async ensureModulesLoaded(): Promise<TPersonModule[] | null> {
          const result = await requestModules();
          return result.modules;
        },

        async loadPersonModules(): Promise<TPersonModule[] | null> {
          return this.ensureModulesLoaded();
        },

        async refreshModules(): Promise<TPersonModule[] | null> {
          const result = await requestModules({ force: true });
          return result.modules;
        },

        async confirmModuleAccess(
          requiredModules: Array<SystemModules | string>
        ): Promise<TModuleAccessCheckResult> {
          if (isAdminScope() || !requiredModules.length) {
            return { status: 'allowed', modules: store.modules() };
          }

          const attempt = await requestModules();
          if (attempt.status === 'error') {
            return {
              status: 'transient_error',
              modules: attempt.modules,
              error: attempt.error,
            };
          }

          if (hasRequiredModules(attempt.modules, requiredModules)) {
            return { status: 'allowed', modules: attempt.modules };
          }

          return { status: 'missing', modules: attempt.modules };
        },

        isModuleAllowed: (moduleId: SystemModules | string): boolean => {
          if (isAdminScope()) {
            return true;
          }
          return store.allowedModuleIds().has(String(moduleId));
        },

        isAnyModuleAllowed: (requiredModules: Array<SystemModules | string>): boolean => {
          if (isAdminScope()) {
            return true;
          }
          const allowedIds = store.allowedModuleIds();
          return requiredModules.some((moduleId) => allowedIds.has(String(moduleId)));
        },

        isSystemAllowed: (requiredModules: Array<SystemModules | string>): boolean => {
          if (isAdminScope()) {
            return true;
          }
          const allowedIds = store.allowedModuleIds();
          return requiredModules.every((moduleId) => allowedIds.has(String(moduleId)));
        },

        getAllowedModules: (): TPersonModule[] => {
          return store.activeModules();
        },

        hasAnyActiveModule: (): boolean => {
          return store.hasActiveModules();
        },

        hasRequiredModules: (requiredModules?: Array<SystemModules | string>): boolean => {
          if (!requiredModules?.length) return true;
          return requiredModules.every((moduleId) => {
            if (isAdminScope()) return true;
            return store.allowedModuleIds().has(String(moduleId));
          });
        },

        hasRequiredAnyModules: (requiredAnyModules?: Array<SystemModules | string>): boolean => {
          if (!requiredAnyModules?.length) return true;
          if (isAdminScope()) return true;
          return requiredAnyModules.some((moduleId) => store.allowedModuleIds().has(String(moduleId)));
        },

        /**
         * Carrega ate quando cada produto esta disponivel. Separado do `ensureModulesLoaded` de
         * proposito: aquele corre no arranque de toda a aplicacao e gateia a navegacao inteira;
         * este so interessa a quem vai mostrar um prazo, e nao tem por que atrasar o arranque.
         *
         * O erro nao vai para o `ErrorStore`: e uma leitura de fundo, e um alerta a dizer que nao
         * se conseguiu saber o prazo seria pior do que nao mostrar a faixa.
         *
         * Sem argumento: o servidor resolve a pessoa pelo Bearer. Antes havia uma guarda contra id
         * vazio; agora nao ha id nenhum para estar vazio, e quem chama nao tem de o descobrir.
         */
        async loadProductsValidity(): Promise<void> {
          patchState(store, setPropLoading('loadProductsValidity'));

          try {
            const productsValidity = await firstValueFrom(
              systemModulesService.getProductsValidity()
            );
            patchState(
              store,
              { productsValidity: productsValidity ?? [] },
              setPropLoaded('loadProductsValidity')
            );
          } catch (error) {
            patchState(store, setPropError('loadProductsValidity', error));
          }
        },
      };
    }
  ),
  withHooks({
    onInit(store, authStore = inject(AuthStore)) {
      // Modules are reloaded only on bearer-token transitions: bootstrap (cache miss
      // on first ensureModulesLoaded) and TokenInterceptor refresh (patches authData
      // with a new TokenId). Temporary auth swaps don't reflect a real bearer change.
      let lastTokenId: string | null = authStore.authData()?.TokenId ?? null;

      effect(() => {
        const auth = authStore.authData();
        const currentTokenId = auth?.TokenId ?? null;

        untracked(() => {
          if (authStore.isTemporaryAuthActive()) {
            return;
          }
          if (currentTokenId === lastTokenId) {
            return;
          }
          lastTokenId = currentTokenId;
          if (currentTokenId) {
            patchState(store, { hasSuccessfulSnapshot: false });
          }
        });
      });
    },
  })
);
